import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { styled, type SxProps, type Theme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

const StyledSvgIcon = styled(SvgIcon)(() => ({
  boxSizing: 'content-box',
  padding: 3,
  fontSize: '1.125rem',
}))

const spinSx = {
  animation: 'spin 2s linear infinite',
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
}

// `sx` is SvgIcon's own type, restated so react/prop-types can see it through the intersection.
export type IconProps = { icon: IconDefinition; spin?: boolean; sx?: SxProps<Theme> } & React.ComponentProps<
  typeof SvgIcon
>

function IconInner(props: IconProps, ref: React.ForwardedRef<SVGSVGElement>) {
  const { icon, spin, sx = {}, ...res } = props

  const {
    icon: [width, height, , , svgPathData],
  } = icon

  return (
    <StyledSvgIcon
      ref={ref}
      viewBox={`0 0 ${width} ${height}`}
      {...res}
      // An array, not a spread: spreading drops a callback or array `sx` without a word.
      sx={[...(Array.isArray(sx) ? sx : [sx]), spin ? spinSx : false]}>
      {typeof svgPathData === 'string' ? (
        <path d={svgPathData} />
      ) : (
        /**
         * A multi-path Font Awesome icon seems to imply a duotune icon. The 0th path seems to
         * be the faded element (referred to as the "secondary" path in the Font Awesome docs)
         * of a duotone icon. 40% is the default opacity.
         *
         * @see https://fontawesome.com/how-to-use/on-the-web/styling/duotone-icons#changing-opacity
         */
        svgPathData.map((d, i) => <path key={i} style={{ opacity: i === 0 ? 0.4 : 1 }} d={d} />)
      )}
    </StyledSvgIcon>
  )
}

export const Icon = React.forwardRef(IconInner)
export type { IconDefinition }
